/**
 * "Goals" and "Engagement": the two lists at the top of the results page.
 *
 * Goals: every goal the test can be read on (Key clicks, Click-through, form
 * submissions, page visits, link click-throughs, custom click sets, Stripe
 * goals). Clicking a row makes it the goal: one interaction, no separate star
 * button. The Key clicks pencil edits which clicks count; "+ Add goal" adds a
 * click set, a page visit or a link click-through. Without Stripe, one
 * Connect Stripe row stands in for trials, payments and revenue.
 *
 * Engagement: bounce rate, time on page and scroll depth for the leading
 * version, read-only, with a better / worse tag.
 */

import { useState, type ComponentProps, type ReactNode } from "react"
import { Link } from "react-router-dom"
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowLeft01Icon, PencilEdit02Icon, PlusSignIcon } from "@hugeicons/core-free-icons"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ClickSetEditor, ClickSetPanel } from "@/components/experiments/results/ClickSetEditor"
import { Hint, RESULT_TIPS, ResultRows, type ResultRow } from "@/components/experiments/results/ResultRows"

type PanelProps = Omit<ComponentProps<typeof ClickSetPanel>, "onCancel">

export type DestinationKind = "page" | "link"

type Props = {
  rows: ResultRow[]
  /** Key of the Key clicks row: it gets the pencil. */
  keyClicksKey: string
  selectedKey: string
  onSelect: (key: string) => void
  ratesHeader: string
  multi: boolean
  loading?: boolean
  readOnly: boolean
  /** Why goals can't be edited (older backend), as a tooltip. */
  editDisabledReason: string | null
  connectStripeHref: string | null
  clickEditor: PanelProps & { open: boolean; onOpenChange: (open: boolean) => void; onEditKeyClicks: () => void; onAddClicks: () => void }
  onAddDestination: (value: string, kind: DestinationKind) => void
}

/** Goals and Engagement share one title row: 15/600 title, a 12px meta line on the right. */
function CardTitle({ title, meta }: { title: string; meta: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 px-5 pt-4 pb-2.5">
      <h2 className="text-[15px] font-semibold leading-5 text-foreground">{title}</h2>
      <span className="hidden text-xs leading-4 text-ui-meta sm:block">{meta}</span>
    </div>
  )
}

export function GoalsCard({
  rows,
  keyClicksKey,
  selectedKey,
  onSelect,
  ratesHeader,
  multi,
  loading,
  readOnly,
  editDisabledReason,
  connectStripeHref,
  clickEditor,
  onAddDestination,
}: Props) {
  // Which popover the shared click editor opens in.
  const [anchor, setAnchor] = useState<"row" | "add">("row")
  const { open, onOpenChange, onEditKeyClicks, onAddClicks, ...panel } = clickEditor

  const pencil = (
    <Button
      variant="ghost"
      size="icon"
      aria-label="Choose which clicks count"
      disabled={Boolean(editDisabledReason)}
      onClick={(e) => {
        e.stopPropagation()
        setAnchor("row")
        onEditKeyClicks()
      }}
      className="size-6 text-muted-foreground hover:text-foreground"
    >
      <HugeiconsIcon icon={PencilEdit02Icon} size={13} />
    </Button>
  )
  const withActions = rows.map((r) =>
    r.key === keyClicksKey && !readOnly
      ? {
          ...r,
          action: (
            <ClickSetEditor
              {...panel}
              open={open && anchor === "row"}
              onOpenChange={onOpenChange}
              onCancel={() => onOpenChange(false)}
              disabledReason={editDisabledReason}
              trigger={pencil}
              hint="Choose which clicks count"
            />
          ),
        }
      : r,
  )

  const footer: ReactNode = (
    <>
      {connectStripeHref && (
        <div className="flex min-h-11 items-center justify-between gap-3 border-t border-border/50 px-2 py-2">
          <span className="text-[13px] text-muted-foreground">Trials, payments and revenue</span>
          <Button asChild variant="outline" className="h-7 rounded-lg px-2.5 text-xs font-medium">
            <Link to={connectStripeHref}>Connect Stripe</Link>
          </Button>
        </div>
      )}
      {!readOnly && (
        <AddGoal
          disabledReason={editDisabledReason}
          clicksOpen={open && anchor === "add"}
          onOpenClicks={() => {
            setAnchor("add")
            onAddClicks()
          }}
          onCloseClicks={() => onOpenChange(false)}
          panel={panel}
          onAddDestination={onAddDestination}
        />
      )}
    </>
  )

  return (
    <Card className="gap-0 py-0">
      <CardTitle title="Goals" meta={readOnly ? "The goals this test can be read on" : "Pick a goal to read the test on it"} />
      <ResultRows
        label="Goals"
        rows={withActions}
        nameHeader="Goal"
        ratesHeader={ratesHeader}
        confidenceTip={multi ? RESULT_TIPS.confidenceMulti : RESULT_TIPS.confidence}
        selectedKey={selectedKey}
        onSelect={onSelect}
        loading={loading}
        footer={footer}
      />
    </Card>
  )
}

function AddGoal({
  disabledReason,
  clicksOpen,
  onOpenClicks,
  onCloseClicks,
  panel,
  onAddDestination,
}: {
  disabledReason: string | null
  clicksOpen: boolean
  onOpenClicks: () => void
  onCloseClicks: () => void
  panel: PanelProps
  onAddDestination: (value: string, kind: DestinationKind) => void
}) {
  const [step, setStep] = useState<"menu" | DestinationKind>("menu")
  const [menuOpen, setMenuOpen] = useState(false)
  const [value, setValue] = useState("")
  const open = menuOpen || clicksOpen

  const trigger = (
    <Button
      variant="ghost"
      disabled={Boolean(disabledReason)}
      className="h-9 w-full justify-start gap-1.5 rounded-lg px-2 text-[13px] font-normal text-muted-foreground hover:text-foreground"
    >
      <HugeiconsIcon icon={PlusSignIcon} size={14} className="text-ui-secondary" />
      Add goal
    </Button>
  )
  if (disabledReason) {
    return (
      <div className="border-t border-border/50 pt-1">
        <Hint text={disabledReason}>
          <span tabIndex={0} className="block w-fit">
            {trigger}
          </span>
        </Hint>
      </div>
    )
  }

  const submit = () => {
    const v = value.trim()
    if (!v || step === "menu") return
    onAddDestination(v, step)
    setMenuOpen(false)
    setStep("menu")
    setValue("")
  }

  return (
    <div className="border-t border-border/50 pt-1">
      <Popover
        open={open}
        onOpenChange={(next) => {
          if (next) {
            setStep("menu")
            setMenuOpen(true)
          } else {
            setMenuOpen(false)
            onCloseClicks()
          }
        }}
      >
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={clicksOpen ? "w-[460px] max-w-[calc(100vw-2rem)] p-4" : "w-[300px] p-1.5"}
        >
          {clicksOpen ? (
            <ClickSetPanel {...panel} onCancel={onCloseClicks} />
          ) : step === "menu" ? (
            <div className="flex flex-col" role="menu">
              {[
                { label: "Clicks on elements…", run: () => { setMenuOpen(false); onOpenClicks() } },
                { label: "Visits a page…", run: () => setStep("page") },
                { label: "Clicks through to a link…", run: () => setStep("link") },
              ].map((item) => (
                <button
                  key={item.label}
                  type="button"
                  role="menuitem"
                  onClick={item.run}
                  className="rounded-md px-2.5 py-2 text-left text-[13px] text-foreground hover:bg-muted"
                >
                  {item.label}
                </button>
              ))}
            </div>
          ) : (
            <form
              className="flex flex-col gap-2 p-1.5"
              onSubmit={(e) => {
                e.preventDefault()
                submit()
              }}
            >
              <button type="button" onClick={() => setStep("menu")} className="flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                <HugeiconsIcon icon={ArrowLeft01Icon} size={12} />
                {step === "page" ? "Visits a page" : "Clicks through to a link"}
              </button>
              <Input
                autoFocus
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={step === "page" ? "/thank-you" : "cal.com/your-team"}
                aria-label={step === "page" ? "Page path" : "Link"}
                className="h-8 text-[13px]"
              />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setMenuOpen(false)} className="h-8 px-3 text-xs">
                  Cancel
                </Button>
                <Button type="submit" disabled={!value.trim()} className="h-8 px-3 text-xs">
                  Add goal
                </Button>
              </div>
            </form>
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}

export function EngagementCard({ rows, ratesHeader, loading }: { rows: ResultRow[]; ratesHeader: string; loading?: boolean }) {
  if (rows.length === 0) return null
  return (
    <Card className="gap-0 py-0">
      <CardTitle title="Engagement" meta="Read-only · how visitors behaved on each version" />
      <ResultRows
        label="Engagement"
        rows={rows}
        nameHeader="Metric"
        ratesHeader={ratesHeader}
        confidenceTip={RESULT_TIPS.engagementConfidence}
        loading={loading}
      />
    </Card>
  )
}
